import { useState, useEffect } from "react";
import posthog from "@/lib/posthog";
import { trackEvent } from "@/lib/analytics";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AppHeader } from "@/components/app-header";
import { AppFooter } from "@/components/app-footer";
import { UpgradeModal } from "@/components/upgrade-modal";
import { Check, Calendar, Infinity, Loader2 } from "lucide-react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";

const freeTierFeatures = [
  "Mirror Mode",
  "L/R Overlay",
  "Speed Control (0.25x–2x)",
  "Fullscreen Practice",
  "A-B Looping",
  "Save unlimited YouTube links",
  "Save 1 uploaded video",
];

const paidFeatures = [
  "All Free Features",
  "120 min uploaded video storage",
  "Audio Practice Player (tempo, pitch, loops)",
  "Notes on saved videos",
  "Timestamped annotations",
  "Playlists to organize your library",
];

export default function Pricing() {
  useEffect(() => { document.title = "Pricing | DanceMirror"; }, []);
  const auth = useAuth();
  const [, setLocation] = useLocation();
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [loginModalOpen, setLoginModalOpen] = useState(false);

  const { data: pricesData } = useQuery<{ prices: any[] }>({
    queryKey: ["/api/stripe/prices"],
  });

  const monthlyPrice = pricesData?.prices?.find(
    (p: any) => p.recurring?.interval === "month"
  );
  const lifetimePrice = pricesData?.prices?.find(
    (p: any) => !p.recurring && p.price_metadata?.tier === "lifetime"
  );

  const isPaid = ["monthly", "yearly", "lifetime"].includes(auth.user?.subscriptionTier ?? "");

  const handleGetStarted = () => {
    setLocation("/");
    setTimeout(() => {
      const el = document.getElementById("get-started");
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  };

  const handleSubscribe = async (priceId: string, plan: "monthly" | "lifetime") => {
    if (!auth.isAuthenticated) {
      posthog.capture('signup_prompt_shown', { trigger: 'checkout' });
      setLoginModalOpen(true);
      return;
    }

    setCheckoutLoading(priceId);
    try {
      const res = await apiRequest("POST", "/api/stripe/checkout", { priceId });
      const data = await res.json();
      if (data.url) {
        posthog.capture('checkout_started', { price_id: priceId });
        trackEvent("checkout_started", { plan });
        window.location.href = data.url;
      }
    } catch (error) {
      console.error("Checkout error:", error);
      setCheckoutLoading(null);
    }
  };

  const handleManageBilling = async () => {
    try {
      const res = await apiRequest("POST", "/api/stripe/portal");
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error("Portal error:", error);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />

      <main className="max-w-5xl mx-auto px-4 py-12 sm:py-20">
        <div className="text-center space-y-3 mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            Simple, <span className="text-accent-foreground">transparent</span> pricing
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg max-w-lg mx-auto">
            Start free, subscribe monthly — or pay once for lifetime access.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 max-w-4xl mx-auto">
          {/* Free card */}
          <Card className="relative p-6 sm:p-8 flex flex-col" data-testid="card-free-tier">
            <div className="mb-6">
              <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center mb-4">
                <Check className="w-5 h-5 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-semibold mb-1">Free</h3>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-4xl font-bold">$0</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Mirror and practice with any video. Create a free account to save your favorites.
              </p>
            </div>

            <ul className="space-y-3 mb-8 flex-1">
              {freeTierFeatures.map((feature) => (
                <li key={feature} className="flex items-center gap-2.5 text-sm">
                  <Check className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="text-muted-foreground">{feature}</span>
                </li>
              ))}
            </ul>

            <Button variant="outline" className="w-full" data-testid="button-free-cta" onClick={handleGetStarted}>
              Get Started
            </Button>
          </Card>

          {/* Monthly card */}
          <Card className="relative p-6 sm:p-8 flex flex-col border-primary" data-testid="card-monthly-tier">
            <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground no-default-hover-elevate no-default-active-elevate" data-testid="badge-popular">
              POPULAR
            </Badge>
            <div className="mb-6">
              <div className="w-10 h-10 rounded-md bg-primary/15 flex items-center justify-center mb-4">
                <Calendar className="w-5 h-5 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-1">Monthly</h3>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-4xl font-bold">$5</span>
                <span className="text-muted-foreground text-sm">/month</span>
              </div>
              <p className="text-sm text-muted-foreground">
                7-day free trial. Cancel anytime.
              </p>
            </div>

            <ul className="space-y-3 mb-8 flex-1">
              {paidFeatures.map((feature) => (
                <li key={feature} className="flex items-center gap-2.5 text-sm">
                  <Check className="w-4 h-4 text-primary shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            {isPaid ? (
              <Button variant="outline" className="w-full" data-testid="button-monthly-manage" onClick={handleManageBilling}>
                Manage Subscription
              </Button>
            ) : (
              <Button
                className="w-full"
                data-testid="button-monthly-cta"
                disabled={!monthlyPrice || checkoutLoading === monthlyPrice?.price_id}
                onClick={() => monthlyPrice && handleSubscribe(monthlyPrice.price_id, "monthly")}
              >
                {checkoutLoading === monthlyPrice?.price_id ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Start Free Trial
              </Button>
            )}
          </Card>

          {/* Lifetime card */}
          <Card className="relative p-6 sm:p-8 flex flex-col" data-testid="card-lifetime-tier">
            <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent text-accent-foreground no-default-hover-elevate no-default-active-elevate" data-testid="badge-best-value">
              BEST VALUE
            </Badge>
            <div className="mb-6">
              <div className="w-10 h-10 rounded-md bg-accent flex items-center justify-center mb-4">
                <Infinity className="w-5 h-5 text-accent-foreground" />
              </div>
              <h3 className="text-xl font-semibold mb-1">Lifetime</h3>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-4xl font-bold">$50</span>
                <span className="text-muted-foreground text-sm">one-time</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Pay once, use forever.
              </p>
            </div>

            <ul className="space-y-3 mb-8 flex-1">
              {paidFeatures.map((feature) => (
                <li key={feature} className="flex items-center gap-2.5 text-sm">
                  <Check className="w-4 h-4 text-primary shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            {isPaid ? (
              <Button variant="outline" className="w-full" data-testid="button-lifetime-manage" onClick={handleManageBilling}>
                Manage Subscription
              </Button>
            ) : (
              <Button
                className="w-full"
                data-testid="button-lifetime-cta"
                disabled={!lifetimePrice || checkoutLoading === lifetimePrice?.price_id}
                onClick={() => lifetimePrice && handleSubscribe(lifetimePrice.price_id, "lifetime")}
              >
                {checkoutLoading === lifetimePrice?.price_id ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Get Lifetime Access
              </Button>
            )}
          </Card>
        </div>

        <p className="text-center text-sm text-muted-foreground mt-10 sm:mt-14">
          All paid plans include a 100% money-back guarantee. No questions asked.
        </p>
      </main>

      <UpgradeModal
        open={loginModalOpen}
        onOpenChange={setLoginModalOpen}
        mode="login"
        featureName="subscribing"
        returnTo="/pricing"
      />

      <AppFooter />
    </div>
  );
}
